import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Supplier, SupplierChannel } from '@core/types';
import Layout from '../componentes/Layout';
import Carregando from '../componentes/Carregando';
import Aviso from '../componentes/Aviso';
import {
  atualizarFornecedor, criarFornecedor, listarFornecedores, type DadosFornecedor,
} from '../lib/consultas';
import { brl, paraNumero } from '../lib/formato';
import { dominioDe, normalizarSite } from '../lib/link';
import { mensagemDeErro } from '../lib/supabase';

// Tela 24 — Fornecedores. Gerente e dono.
//
// A tabela `suppliers` existe desde a base v1 e a Entrada de mercadoria já lê
// dela: com o fornecedor informado, o custo da entrada vira também a COTAÇÃO
// dele em `supplier_products` — que é o que vai alimentar o roteamento de
// pedidos da Fase 2 e o "quem vende mais barato" dos relatórios. O que faltava
// era como preencher: a lista da Entrada apontava para uma tabela que só o SQL
// alcançava.
//
// POR QUE GERENTE TAMBÉM, E NÃO SÓ O DONO: quem recebe a mercadoria é quem
// conhece o fornecedor. Fechar isso no dono significaria que uma entrega de um
// fornecedor novo, numa terça de manhã, fica parada até ele cadastrar — e
// entrada parada vira estoque errado. A RLS já decide igual (rls.sql §1:
// insert/update de gerente e dono, delete só do dono); esta tela espelha.
//
// DESATIVAR, NÃO EXCLUIR: fornecedor com cotação e histórico de compra apagado
// levaria `supplier_products` junto por cascata — e com ele o custo histórico
// que sustenta relatório de margem. Inativo some da Entrada e continua no
// passado. Por isso não existe botão de excluir aqui, mesmo o dono podendo.

const CANAIS: Array<{ id: SupplierChannel; rotulo: string }> = [
  { id: 'whatsapp', rotulo: 'WhatsApp' },
  { id: 'email', rotulo: 'E-mail' },
  { id: 'telefone', rotulo: 'Telefone' },
  { id: 'portal', rotulo: 'Portal do fornecedor' },
  { id: 'outro', rotulo: 'Outro' },
];

const VAZIO: DadosFornecedor = {
  nome: '', cidade: '', cnpj: '', canal_preferencial: 'whatsapp',
  whatsapp: '', email: '', site: '', lead_time_dias: 3, pedido_minimo: 0,
  observacoes: '', ativo: true,
};

export default function Fornecedores() {
  const cliente = useQueryClient();
  const [criando, setCriando] = useState(false);
  const [editando, setEditando] = useState<string | null>(null);

  const lista = useQuery({ queryKey: ['fornecedores-todos'], queryFn: listarFornecedores });

  const recarregar = () => {
    void cliente.invalidateQueries({ queryKey: ['fornecedores-todos'] });
    // A lista da Entrada de mercadoria é outra consulta, com outra chave: sem
    // isto o fornecedor recém-cadastrado só apareceria lá na próxima sessão.
    void cliente.invalidateQueries({ queryKey: ['fornecedores'] });
  };

  const ativos = (lista.data ?? []).filter((f) => f.ativo);
  const inativos = (lista.data ?? []).filter((f) => !f.ativo);

  return (
    <Layout
      titulo="Fornecedores"
      acao={
        <button
          type="button"
          onClick={() => { setCriando((v) => !v); setEditando(null); }}
          className="botao-primario px-3 py-2 text-xs"
        >
          {criando ? 'Fechar' : '+ Fornecedor'}
        </button>
      }
    >
      <div className="space-y-4">
        {criando && (
          <Formulario
            titulo="Novo fornecedor"
            inicial={VAZIO}
            permiteInativar={false}
            aoSalvar={(d) => criarFornecedor(d)}
            aoPronto={() => { setCriando(false); recarregar(); }}
          />
        )}

        {lista.isLoading ? <Carregando /> : lista.error ? (
          <Aviso tipo="erro">{mensagemDeErro(lista.error)}</Aviso>
        ) : (lista.data ?? []).length === 0 ? (
          <div className="cartao p-6 text-center">
            <p className="font-semibold">Nenhum fornecedor cadastrado ainda.</p>
            <p className="mt-1 text-sm text-mute">
              Cadastre os que a loja já compra — Skypeças, Roles, MGA, Dobler, Real Motopeças —
              e eles passam a aparecer na Entrada de mercadoria. Informar o fornecedor na
              entrada é o que guarda o custo dele por peça.
            </p>
          </div>
        ) : (
          <>
            {ativos.map((f) => (
              <Cartao key={f.id} fornecedor={f}
                      aberto={editando === f.id}
                      aoAbrir={() => setEditando(editando === f.id ? null : f.id)}
                      aoPronto={() => { setEditando(null); recarregar(); }} />
            ))}

            {inativos.length > 0 && (
              <>
                <p className="rotulo pt-2">Inativos</p>
                {inativos.map((f) => (
                  <Cartao key={f.id} fornecedor={f}
                          aberto={editando === f.id}
                          aoAbrir={() => setEditando(editando === f.id ? null : f.id)}
                          aoPronto={() => { setEditando(null); recarregar(); }} />
                ))}
              </>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}

function Cartao({ fornecedor, aberto, aoAbrir, aoPronto }: {
  fornecedor: Supplier;
  aberto: boolean;
  aoAbrir: () => void;
  aoPronto: () => void;
}) {
  const canal = CANAIS.find((c) => c.id === fornecedor.canal_preferencial)?.rotulo
    ?? fornecedor.canal_preferencial;

  return (
    <article className={`cartao ${fornecedor.ativo ? '' : 'opacity-60'}`}>
      <button type="button" onClick={aoAbrir}
              className="flex w-full items-center justify-between gap-3 p-4 text-left">
        <div className="min-w-0">
          <p className="truncate font-semibold">{fornecedor.nome}</p>
          <p className="truncate text-xs text-dim">
            {[fornecedor.cidade, canal, fornecedor.site ? dominioDe(fornecedor.site) : null,
              fornecedor.whatsapp || fornecedor.email].filter(Boolean).join(' · ')}
          </p>
        </div>
        <div className="shrink-0 text-right text-xs text-mute">
          <p>{fornecedor.lead_time_dias} {fornecedor.lead_time_dias === 1 ? 'dia' : 'dias'}</p>
          {Number(fornecedor.pedido_minimo) > 0 && (
            <p className="tabular-nums">mín. {brl(Number(fornecedor.pedido_minimo))}</p>
          )}
        </div>
      </button>

      {aberto && (
        <div className="border-t border-edge p-4">
          <Formulario
            titulo={`Editar ${fornecedor.nome}`}
            inicial={{
              nome: fornecedor.nome,
              cidade: fornecedor.cidade ?? '',
              cnpj: fornecedor.cnpj ?? '',
              canal_preferencial: fornecedor.canal_preferencial,
              whatsapp: fornecedor.whatsapp ?? '',
              email: fornecedor.email ?? '',
              site: fornecedor.site ?? '',
              lead_time_dias: fornecedor.lead_time_dias,
              pedido_minimo: Number(fornecedor.pedido_minimo),
              observacoes: fornecedor.observacoes ?? '',
              ativo: fornecedor.ativo,
            }}
            permiteInativar
            aoSalvar={(d) => atualizarFornecedor(fornecedor.id, d)}
            aoPronto={aoPronto}
          />
        </div>
      )}
    </article>
  );
}

function Formulario({ titulo, inicial, permiteInativar, aoSalvar, aoPronto }: {
  titulo: string;
  inicial: DadosFornecedor;
  permiteInativar: boolean;
  aoSalvar: (d: DadosFornecedor) => Promise<Supplier>;
  aoPronto: () => void;
}) {
  const [f, setF] = useState<DadosFornecedor>(inicial);
  // `pedido_minimo` e `lead_time_dias` moram em texto enquanto a pessoa digita:
  // number controlado apaga o que ela escreveu na hora em que o valor fica
  // intermediário ("1." ao digitar 1.500).
  const [minimo, setMinimo] = useState(
    inicial.pedido_minimo > 0 ? String(inicial.pedido_minimo).replace('.', ',') : '',
  );
  const [prazo, setPrazo] = useState(String(inicial.lead_time_dias));

  const set = <K extends keyof DadosFornecedor>(k: K, v: DadosFornecedor[K]) =>
    setF((a) => ({ ...a, [k]: v }));

  const salvar = useMutation({
    mutationFn: async () => {
      if (!f.nome.trim()) throw new Error('Informe o nome do fornecedor.');
      // paraNumero, e não Number: "1.500" em pt-BR é mil e quinhentos, e
      // Number('1.500') é 1,5 — a armadilha com teste de regressão própria.
      const min = minimo.trim() === '' ? 0 : paraNumero(minimo);
      if (min === null || min < 0) throw new Error('Pedido mínimo inválido.');
      const dias = Number(prazo);
      if (!Number.isInteger(dias) || dias < 0) {
        throw new Error('Prazo de entrega deve ser um número de dias.');
      }
      // undefined = digitou algo que não é endereço navegável. null = deixou
      // vazio, que é legítimo — confundir os dois apagaria o site em silêncio.
      const site = normalizarSite(f.site ?? '');
      if (site === undefined) {
        throw new Error('O site precisa ser um endereço da web (começando com http:// '
          + 'ou https://). Exemplo: mga.com.br');
      }
      return aoSalvar({ ...f, site, pedido_minimo: min, lead_time_dias: dias });
    },
    onSuccess: aoPronto,
  });

  return (
    <section className="space-y-3">
      <p className="font-bold">{titulo}</p>

      <div>
        <label className="rotulo" htmlFor="forn-nome">Nome *</label>
        <input id="forn-nome" className="campo" value={f.nome} autoComplete="off"
               onChange={(e) => set('nome', e.target.value)} placeholder="Skypeças" />
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="rotulo" htmlFor="forn-cidade">Cidade</label>
          <input id="forn-cidade" className="campo" value={f.cidade ?? ''}
                 onChange={(e) => set('cidade', e.target.value)} placeholder="Pelotas" />
        </div>
        <div>
          <label className="rotulo" htmlFor="forn-cnpj">CNPJ</label>
          <input id="forn-cnpj" className="campo" value={f.cnpj ?? ''} inputMode="numeric"
                 onChange={(e) => set('cnpj', e.target.value)} />
        </div>
      </div>

      <div>
        <label className="rotulo" htmlFor="forn-canal">Como se faz o pedido</label>
        <select id="forn-canal" className="campo" value={f.canal_preferencial}
                onChange={(e) => set('canal_preferencial', e.target.value as SupplierChannel)}>
          {CANAIS.map((c) => <option key={c.id} value={c.id}>{c.rotulo}</option>)}
        </select>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="rotulo" htmlFor="forn-zap">WhatsApp</label>
          <input id="forn-zap" className="campo" value={f.whatsapp ?? ''} inputMode="tel"
                 onChange={(e) => set('whatsapp', e.target.value)} placeholder="(53) 99999-0000" />
        </div>
        <div>
          <label className="rotulo" htmlFor="forn-email">E-mail</label>
          <input id="forn-email" className="campo" type="email" value={f.email ?? ''}
                 onChange={(e) => set('email', e.target.value)} />
        </div>
      </div>

      <div>
        <label className="rotulo" htmlFor="forn-site">Site / catálogo</label>
        <input id="forn-site" className="campo" value={f.site ?? ''} inputMode="url"
               onChange={(e) => set('site', e.target.value)} placeholder="mga.com.br" />
        <p className="mt-1 text-xs text-mute">
          Vira link clicável na ficha da peça e no resultado da busca — é por onde se
          confere preço e disponibilidade antes de mandar mensagem. Pode digitar sem o
          "https://".
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="rotulo" htmlFor="forn-prazo">Prazo de entrega (dias)</label>
          <input id="forn-prazo" className="campo" value={prazo} inputMode="numeric"
                 onChange={(e) => setPrazo(e.target.value)} />
          <p className="mt-1 text-xs text-mute">
            Quanto costuma demorar entre pedir e chegar. É o que decide com que antecedência
            repor uma peça que está no mínimo.
          </p>
        </div>
        <div>
          <label className="rotulo" htmlFor="forn-minimo">Pedido mínimo (R$)</label>
          <input id="forn-minimo" className="campo" value={minimo} inputMode="decimal"
                 onChange={(e) => setMinimo(e.target.value)} placeholder="0,00" />
          <p className="mt-1 text-xs text-mute">
            Valor abaixo do qual ele não entrega. Deixe vazio se não houver.
          </p>
        </div>
      </div>

      <div>
        <label className="rotulo" htmlFor="forn-obs">Observações</label>
        <textarea id="forn-obs" className="campo" rows={2} value={f.observacoes ?? ''}
                  onChange={(e) => set('observacoes', e.target.value)}
                  placeholder="entrega às terças e quintas; falar com o Marcelo" />
      </div>

      {permiteInativar && (
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={f.ativo}
                 onChange={(e) => set('ativo', e.target.checked)} />
          <span>
            Fornecedor ativo
            <span className="block text-xs text-mute">
              Desmarcado, ele some da Entrada de mercadoria. O custo já registrado nas peças
              dele continua valendo — nada do passado se perde.
            </span>
          </span>
        </label>
      )}

      {salvar.error ? <Aviso tipo="erro">{mensagemDeErro(salvar.error)}</Aviso> : null}

      <button type="button" className="botao-primario w-full" disabled={salvar.isPending}
              onClick={() => salvar.mutate()}>
        {salvar.isPending ? 'Salvando…' : 'Salvar'}
      </button>
    </section>
  );
}
